import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { adminAPI } from '../api/api';
import { Activity as ActivityIcon, Clock, Filter, RefreshCcw } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Card, Btn, Badge, formatDateTime, Spinner } from '../components/admin/AdminUI';

const COMMON_ACTIONS = [
  '', 'plan.changed', 'member.role_changed', 'member.removed', 'member.active', 'member.disabled',
  'org.created', 'org.deleted', 'org.status_changed', 'subscription.created', 'subscription.canceled',
];

const AdminActivity = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('');
  const [limit, setLimit] = useState(100);

  useEffect(() => { document.title = "Skilldeck — Admin · Activité"; }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminAPI.globalAudit({ limit, action: action || undefined });
      setEntries(data);
    } catch { toast({ title: 'Erreur', description: "Impossible de charger l'activité", variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [action, limit, toast]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout title="Activité">
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-4">

        <Card padding="p-0">
          <div className="px-5 py-4 border-b border-[rgba(108,71,255,.12)] flex items-center gap-3 flex-wrap">
            <ActivityIcon className="h-4 w-4 text-violet-300" />
            <h3 className="text-sm font-semibold text-white">Journal d'activité plateforme</h3>
            <div className="ml-auto flex items-center gap-2">
              <div className="inline-flex items-center gap-2 text-xs text-gray-500"><Filter className="h-3 w-3" /> Action</div>
              <select value={action} onChange={(e) => setAction(e.target.value)}
                className="px-3 py-1.5 bg-[#05050f] border border-[rgba(108,71,255,.15)] rounded-lg text-xs text-white">
                {COMMON_ACTIONS.map(a => <option key={a} value={a}>{a || 'Toutes'}</option>)}
              </select>
              <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}
                className="px-3 py-1.5 bg-[#05050f] border border-[rgba(108,71,255,.15)] rounded-lg text-xs text-white">
                {[50, 100, 200, 500].map(n => <option key={n} value={n}>{n} entrées</option>)}
              </select>
              <Btn tone="subtle" onClick={load}><RefreshCcw className="h-3 w-3" /></Btn>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Spinner /></div>
          ) : entries.length === 0 ? (
            <div className="text-center py-16 text-gray-500 text-sm">Aucune entrée</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[rgba(108,71,255,.1)] text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                    <th className="px-5 py-3">Date</th>
                    <th className="px-5 py-3">Organisation</th>
                    <th className="px-5 py-3">Acteur</th>
                    <th className="px-5 py-3">Action</th>
                    <th className="px-5 py-3">Entité</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[rgba(108,71,255,.06)]">
                  {entries.map((e) => (
                    <tr key={e.id} className="hover:bg-[rgba(108,71,255,.04)] transition-colors">
                      <td className="px-5 py-3 text-xs text-gray-400 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5"><Clock className="h-3 w-3" /> {formatDateTime(e.created_at)}</span>
                      </td>
                      <td className="px-5 py-3 text-sm">
                        {e.organizations?.name ? (
                          <button onClick={() => navigate(`/admin/orgs/${e.organization_id}`)} className="text-gray-200 hover:text-violet-300">
                            {e.organizations.name}
                          </button>
                        ) : <span className="text-gray-500 font-mono text-[11px]">{e.organization_id?.slice(0, 12)}…</span>}
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-300">{e.actor_name || e.actor_user_id?.slice(0, 12) || '—'}</td>
                      <td className="px-5 py-3"><Badge tone="violet">{e.action}</Badge></td>
                      <td className="px-5 py-3 text-xs text-gray-500 font-mono">
                        {e.entity_type}
                        {e.entity_id ? ` ${e.entity_id.slice(0, 8)}…` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminActivity;
